import React, { useState, useCallback, useMemo } from 'react';
import Table from '../../components/common/Table';
import { InvoiceDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import { renderInvoiceTypeBadge, renderInvoiceStatusBadge } from '../../utils/invoiceUtils';
import { FileViewer } from '../common/FileViewer';
import { View } from 'lucide-react';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';

interface InvoiceTableProps {
  data: InvoiceDTO[];
  handleEdit: (row: InvoiceDTO) => void;
  handleDelete: (row: InvoiceDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<{
    url: string;
    number: string;
  } | null>(null);
  const { getInvoiceSasUrl } = useInvoiceApi();

  const handleViewPdf = useCallback(async (invoice: InvoiceDTO) => {
    try {
      const response = await getInvoiceSasUrl(invoice.id!);
      if (response && response.data && response.success) {
        setSelectedInvoice({
          url: response.data,
          number: invoice.invoiceNumber || 'Invoice',
        });
        setViewerOpen(true);
      }
    } catch (error) {
      console.error('Error getting invoice URL:', error);
    }
  }, [getInvoiceSasUrl]);

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    setSelectedInvoice(null);
  }, []);

  const viewerProps = useMemo(() => {
    if (!viewerOpen || !selectedInvoice) return null;
    return {
      fileName: `${selectedInvoice.number}.pdf`,
      sasUrl: selectedInvoice.url,
      onClose: handleCloseViewer,
    };
  }, [viewerOpen, selectedInvoice, handleCloseViewer]);

  return (
    <>
      <Table
        columns={[
          {
            label: 'Inv. Number',
            key: 'invoiceNumber',
          },
          {
            label: 'Type',
            key: 'invoiceTypeId',
            render: (row: InvoiceDTO) => 
              renderInvoiceTypeBadge(row.invoiceTypeId?.toString(), row.invoiceTypeName),
          },
          {
            label: 'Status',
            key: 'invoiceStatusId',
            render: (row: InvoiceDTO) => 
              renderInvoiceStatusBadge(row.invoiceStatusId?.toString(), row.invoiceStatusName),
          },
          {
            label: 'Client',
            key: 'clientName',
          },
          {
            label: 'Subtotal',
            key: 'subtotal',
            format: (value: number) => `R ${value.toFixed(2)}`,
          },
          {
            label: 'VAT',
            key: 'vatAmount',
            render: (row: InvoiceDTO) => (
              <span>
                {row.includeVat 
                  ? `R ${(row.vatAmount || 0).toFixed(2)} (${row.vatRate || 0}%)`
                  : '-'}
              </span>
            ),
          },
          {
            label: 'Total',
            key: 'total',
            format: (value: number) => `R ${value.toFixed(2)}`,
          },
          {
            label: 'Due Date',
            key: 'dueDate',
            format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
          },
          {
            label: 'Added',
            key: 'createdUtc',
            format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
          },
        ]}
        data={data}
        actions={{
          onEdit: handleEdit,
          onDelete: handleDelete,
          extra: (row: InvoiceDTO) => [
            {
              label: 'View PDF',
              onClick: () => handleViewPdf(row),
              icon: <View className="w-4 h-4" />,
            },
          ],
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      />

      {viewerProps && <FileViewer {...viewerProps} />}
    </>
  );
};

export default React.memo(InvoiceTable);
